/**
 * Dashboard admin : conflits fingerprint compétition différée.
 *
 * Affiche les groupes (deal_id × device) avec plusieurs pseudos distincts,
 * permet à un administrateur de :
 *   - marquer comme légitime (whitelist : couple, famille, partage clavier)
 *   - invalider un score suspect
 *   - flagger pour suivi ultérieur
 *
 * Backend : `/api/admin/anti-cheat/conflicts` (auth admin via hook global).
 */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';

interface AntiCheatConflictsPageProps {
  session: Session;
}

interface ConflictRow {
  competition_type: string;
  deal_id: string;
  device_key: string;
  device_source: 'web' | 'native';
  distinct_pseudos: number;
  pseudos: string[];
  first_seen: string;
  last_seen: string;
  max_status: 'recorded' | 'flagged' | 'whitelisted' | 'invalidated';
}

interface ConflictDetailRow {
  id: number;
  pseudo: string;
  visitor_id: string | null;
  native_device_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('pseudo', pseudo);
  return fetch(u.toString(), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Pseudo': pseudo,
      ...(options.headers || {}),
    },
  });
}

export default function AntiCheatConflictsPage({ session }: AntiCheatConflictsPageProps) {
  const [conflicts, setConflicts] = useState<ConflictRow[]>([]);
  const [details, setDetails] = useState<Record<string, ConflictDetailRow[]>>({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');

  const loadConflicts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = filterType
        ? `${BACKEND_URL}/api/admin/anti-cheat/conflicts?competition_type=${encodeURIComponent(filterType)}`
        : `${BACKEND_URL}/api/admin/anti-cheat/conflicts`;
      const res = await adminFetch(url, session.pseudo);
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      setConflicts(data.conflicts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [session.pseudo, filterType]);

  useEffect(() => {
    void loadConflicts();
  }, [loadConflicts]);

  const handleExpand = async (key: string, conflict: ConflictRow) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (!details[key]) {
      try {
        const url = `${BACKEND_URL}/api/admin/anti-cheat/conflicts/detail`
          + `?competition_type=${encodeURIComponent(conflict.competition_type)}`
          + `&deal_id=${encodeURIComponent(conflict.deal_id)}`
          + `&device_key=${encodeURIComponent(conflict.device_key)}`;
        const res = await adminFetch(url, session.pseudo);
        if (res.ok) {
          const data = await res.json();
          setDetails(prev => ({ ...prev, [key]: data.rows || [] }));
        }
      } catch {
        // Endpoint detail optionnel - silencieux
      }
    }
  };

  const handleAction = async (rowId: number, action: 'whitelist' | 'invalidate' | 'flag') => {
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/anti-cheat/conflicts/${rowId}/action`,
        session.pseudo,
        { method: 'POST', body: JSON.stringify({ action }) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadConflicts();
    } catch (err) {
      alert(`Action échouée : ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (loading) return <div className="admin-loading">Chargement des conflits…</div>;
  if (error) return <div className="admin-empty">Erreur : {error}</div>;

  return (
    <div className="admin-section">
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          Conflits fingerprint compétition <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({conflicts.length})</span>
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Filtrer :</label>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6 }}
          >
            <option value="">Tous types</option>
            <option value="differe">Différé</option>
            <option value="cia">CIA</option>
            <option value="team_champ">Championnat équipes</option>
          </select>
          <button type="button" onClick={() => void loadConflicts()}>Rafraîchir</button>
        </div>
      </header>

      {conflicts.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>
          Aucun conflit détecté. Tout va bien.
        </p>
      ) : (
        <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Type</th>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Donne</th>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Device</th>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Pseudos en conflit</th>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Première fois</th>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Dernière fois</th>
              <th style={{ textAlign: 'left', padding: '8px 12px' }}>Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {conflicts.map((c) => {
              const key = `${c.competition_type}|${c.deal_id}|${c.device_key}`;
              const isExpanded = expandedKey === key;
              return (
                <>
                  <tr key={key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '8px 12px' }}>{c.competition_type}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{c.deal_id}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11 }}>
                      <span style={{
                        background: c.device_source === 'native' ? '#dbeafe' : '#fef3c7',
                        color: c.device_source === 'native' ? '#1e40af' : '#92400e',
                        padding: '2px 6px', borderRadius: 4, marginRight: 6, fontSize: 10,
                      }}>{c.device_source}</span>
                      {c.device_key.substring(0, 12)}…
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <strong>{c.distinct_pseudos}</strong>
                      <small style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>
                        ({c.pseudos.slice(0, 3).join(', ')}{c.pseudos.length > 3 ? '…' : ''})
                      </small>
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{new Date(c.first_seen).toLocaleString('fr-FR')}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12 }}>{new Date(c.last_seen).toLocaleString('fr-FR')}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 12,
                        background: c.max_status === 'whitelisted' ? '#d1fae5'
                          : c.max_status === 'invalidated' ? '#fee2e2'
                          : c.max_status === 'flagged' ? '#fef3c7' : '#e5e7eb',
                        color: c.max_status === 'whitelisted' ? '#065f46'
                          : c.max_status === 'invalidated' ? '#991b1b'
                          : c.max_status === 'flagged' ? '#92400e' : '#374151',
                      }}>{c.max_status}</span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <button type="button" onClick={() => handleExpand(key, c)} style={{ fontSize: 12 }}>
                        {isExpanded ? 'Replier' : 'Détail'}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} style={{ background: 'var(--bg-tertiary)', padding: 16 }}>
                        {details[key] === undefined ? (
                          <div>Chargement du détail…</div>
                        ) : details[key].length === 0 ? (
                          <div style={{ color: 'var(--text-secondary)' }}>
                            <p>Endpoint de détail non disponible (pas encore implémenté).</p>
                            <p>Actions globales sur ce conflit :</p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button type="button" onClick={() => alert('Sélectionner un enregistrement individuel pour appliquer une action ciblée.')}>
                                Whitelist (couple/famille)
                              </button>
                            </div>
                          </div>
                        ) : (
                          <table style={{ width: '100%', fontSize: 13 }}>
                            <thead>
                              <tr><th>Pseudo</th><th>IP</th><th>UA</th><th>Date</th><th>Statut</th><th>Actions</th></tr>
                            </thead>
                            <tbody>
                              {details[key].map(d => (
                                <tr key={d.id}>
                                  <td>{d.pseudo}</td>
                                  <td>{d.ip_address || '—'}</td>
                                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.user_agent || '—'}</td>
                                  <td>{new Date(d.created_at).toLocaleString('fr-FR')}</td>
                                  <td>{d.status}</td>
                                  <td>
                                    <button type="button" onClick={() => handleAction(d.id, 'whitelist')}>OK</button>{' '}
                                    <button type="button" onClick={() => handleAction(d.id, 'invalidate')}>Invalider</button>{' '}
                                    <button type="button" onClick={() => handleAction(d.id, 'flag')}>Flagger</button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
